// User service. Profile management, onboarding status, FCM token registration, and user preferences.
import { authFetch, API_URL } from './apiClient';
import { safeLog } from '@/utils/safeLog';

export interface UserOnboardingData {
  hasCompletedOnboarding: boolean;
  onboardingCompletedAt: string;
  notificationPermissions: boolean;
  pushNotificationsEnabled: boolean;
}

export interface UserProfile {
  id: string;
  role: string;
  name: string;
  email: string;
  hasCompletedOnboarding: boolean;
  onboardingCompletedAt?: string;
  notificationPermissions: boolean;
  pushNotificationsEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

class UserService {
  async updateOnboardingStatus(
    userId: string, 
    data: {
      notificationsGranted: boolean;
      permissions: Record<string, boolean>;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await authFetch(`/users/${userId}/onboarding`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to update onboarding status' };
      }

      return { success: true };
    } catch (error) {
      safeLog.error('Exception updating user onboarding status:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async getUserProfile(userId: string): Promise<{ user?: UserProfile; error?: string }> {
    try {
      const response = await authFetch(`/users/${userId}`);
      const result = await response.json();

      if (!response.ok) {
        return { error: result.error || 'Failed to fetch user profile' };
      }

      return { user: result.user };
    } catch (error) {
      return { 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async createUserProfile(
    authUid: string,
    email: string,
    name: string,
    role: 'student' | 'teacher' | 'admin'
  ): Promise<{ success: boolean; user?: UserProfile; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authUid,
          email,
          name,
          role,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // If user already exists (409), that's okay - the trigger created them
        if (response.status === 409) {
          return { success: true };
        }
        return { success: false, error: result.error || 'Failed to create user profile' };
      }

      return { success: true, user: result.user };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async updateNotificationPreferences(
    userId: string,
    preferences: {
      notificationPermissions: boolean;
      pushNotificationsEnabled: boolean;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await authFetch(`/users/${userId}/notifications`, {
        method: 'PATCH',
        body: JSON.stringify(preferences),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to update notification preferences' };
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async registerFCMToken(
    userId: string,
    fcmToken: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await authFetch(`/users/${userId}/fcm-token`, {
        method: 'POST',
        body: JSON.stringify({ fcmToken }),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to register FCM token' };
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async sendTestNotification(
    userId: string,
    fcmToken: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await authFetch(`/users/${userId}/test-notification`, {
        method: 'POST',
        body: JSON.stringify({ fcmToken }),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to send test notification' };
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }
}

export const userService = new UserService();
